import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import useFileAccess from "files/hooks/useFileAccess";
import useFileJobs from "files/hooks/useFileJobs";
import useFiles, {
  resolveObjectKey,
  resolveRouterPath,
  useFilesSearch,
} from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileInfo from "files/types/interfaces/fileInfo";
import { JobStatus, JobType } from "files/types/interfaces/fileJob";
import { StringParam, useQueryParam } from "use-query-params";
import useUser from "~/contexts/user";

const { VITE_RWB_API } = import.meta.env;

const splitKey = (key: string) => {
  const split = key.split("/").filter(Boolean);
  const prefix = split.slice(0, split.length - 1).join("/");
  const filename = split.at(-1) ?? "";
  return [prefix, filename];
};

export interface UseFileActionDialogProps {
  action: JobType;
  isDirectory: boolean;
  fileKey: string;
  handleClose: () => void;
}

export const useFileActionDialog = ({
  action,
  fileKey,
  isDirectory,
  handleClose,
}: UseFileActionDialogProps) => {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [search] = useQueryParam("q", StringParam);
  const { data: jobs, mutate: mutateJobs } = useFileJobs();
  const { data: filesData, mutate: mutateFiles } = useFiles();
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const [prefix, originalName] = splitKey(fileKey);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const destination = selectedFile?.key ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const destinationKey = resolveObjectKey(destination, user?.email);
  const canWrite = useFileAccess(destinationKey, "write");
  const form = useForm({
    mode: "onChange",
    defaultValues: { fileSystemNodeName: originalName },
  });
  const fileSystemNodeName = isDirectory ? "Folder" : "File";

  const submitDisabled = useMemo(() => {
    return (
      !destination || // No path selected
      !canWrite || // User does not have permission to write to selected directory
      destination === resolveRouterPath(prefix) || // Current path is the same
      [BaseDirectory.ORG, BaseDirectory.PROJECTS].includes(destination as BaseDirectory) // Virtual directories excluded
    );
  }, [destination, canWrite, prefix]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      const extensionIndex = inputRef.current.value.indexOf(".");
      inputRef.current.setSelectionRange(0, extensionIndex);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    // Use original prefix if renaming, otherwise resolve key from selected path
    const destPrefix = action === JobType.RENAME ? `${prefix}/` : destinationKey;

    const formName = form.getValues("fileSystemNodeName");
    const formattedDest = `${destPrefix}${formName}${isDirectory ? "/" : ""}`;

    const resp = await fetch(`${VITE_RWB_API}/file-browser/jobs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        job_type: action,
        source: fileKey,
        dest: formattedDest,
      }),
    });

    if (resp.ok) {
      handleClose();
      const id = await resp.json();
      // Optimistically update pending jobs
      await mutateJobs([
        ...(jobs ?? []),
        {
          id,
          source: fileKey,
          destination: formattedDest,
          created_by: user?.email,
          status: JobStatus.PENDING,
          job_type: action,
        },
      ]);

      // User is previewing file they just modified
      if (location.state?.preview?.key === fileKey) {
        if (action === JobType.RENAME) {
          // Update file name in preview header
          navigate(".", {
            relative: "path",
            replace: true,
            state: {
              preview: {
                key: formattedDest,
                size: location.state.preview.size,
              },
            },
          });
        }

        if (action === JobType.MOVE) {
          // Close preview modal by clearing preview state
          navigate(".", { relative: "path", replace: true });
        }
      }

      if (search) {
        await mutateFilesSearch();
      }

      await mutateFiles();
    } else {
      const body = await resp.json();
      if (resp.status === 413 && body.message) {
        setError(`Unable to ${action} file. ${body.message} Contact Support.`);
      } else {
        setError(`Unable to ${action} file`);
      }
    }

    setSubmitting(false);
  };

  return {
    filesData,
    selectedFile,
    setSelectedFile,
    fileSystemNodeName,
    handleSubmit,
    submitDisabled,
    submitting,
    originalName,
    form,
    inputRef,
    error,
  };
};

export type UseFileActionDialog = ReturnType<typeof useFileActionDialog>;
